/* /assets/contact-editorial.js
 * <contact-editorial>: field states, inline validation, custom dropdown,
 * submit + success handling for sections/section-contact-editorial.liquid.
 * Messages come from data-msg-* on the element (section settings).
 */
(function () {
  if (customElements.get('contact-editorial')) return;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE_RE = /^[+()\d\s.\-]{6,}$/;

  class ContactEditorial extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-contact-form]');
      if (!this.form) return;

      this.msgs = {
        required: this.dataset.msgRequired || 'This is required',
        email: this.dataset.msgEmail || 'Enter a valid email',
        phone: this.dataset.msgPhone || 'Enter a valid phone number',
        select: this.dataset.msgSelect || 'Pick one',
        consent: this.dataset.msgConsent || 'This is required',
        summary: this.dataset.msgSummary || 'A few fields need attention',
      };

      this.fields = Array.from(this.form.querySelectorAll('[data-field]'));
      this.summary = this.form.querySelector('[data-summary]');
      this.summaryText = this.form.querySelector('[data-summary-text]');
      this.submitBtn = this.form.querySelector('[data-submit]');
      this.submitLabel = this.form.querySelector('[data-submit-label]');
      if (this.submitLabel) this.submitLabel.dataset.label = this.submitLabel.textContent;
      this.honeypot = this.form.querySelector('[data-honeypot]');
      this.routeField = this.form.querySelector('[data-route-field]');
      this.success = this.form.querySelector('[data-success]');
      this.fieldsWrap = this.form.querySelector('[data-fields]');

      this.fields.forEach((field) => this.bindField(field));
      this.form.querySelectorAll('[data-select]').forEach((el) => this.bindSelect(el));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      const again = this.form.querySelector('[data-send-another]');
      if (again) again.addEventListener('click', () => this.reset());

      if (this.success) {
        // Landed here after a successful post: bring the panel into view.
        this.success.focus({ preventScroll: false });
        if (window.location.hash !== '#contact-form') {
          const top = this.form.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top, behavior: 'instant' in window ? 'instant' : 'auto' });
        }
      }
    }

    /* ---------- fields ---------- */

    control(field) {
      return field.querySelector('input:not([type="hidden"]), textarea, select');
    }

    bindField(field) {
      const control = this.control(field);
      if (!control) return;

      this.syncFilled(field, control);

      control.addEventListener('focus', () => field.classList.add('is-active'));
      control.addEventListener('blur', () => {
        field.classList.remove('is-active');
        this.syncFilled(field, control);
        if (field.dataset.touched) this.validate(field);
      });
      control.addEventListener('input', () => {
        this.syncFilled(field, control);
        // Live-clear an error once the value becomes valid.
        if (field.classList.contains('is-error')) this.validate(field);
      });
      control.addEventListener('change', () => {
        this.syncFilled(field, control);
        if (field.dataset.touched || field.classList.contains('is-error')) this.validate(field);
      });
    }

    syncFilled(field, control) {
      const filled = control.type === 'checkbox' ? control.checked : control.value.trim() !== '';
      field.classList.toggle('is-filled', filled);
    }

    validate(field) {
      const control = this.control(field);
      if (!control) return true;
      field.dataset.touched = '1';

      const value = (control.value || '').trim();
      const required = control.hasAttribute('required');
      let message = '';

      if (control.type === 'checkbox') {
        if (required && !control.checked) {
          message = control.name.indexOf('privacy') > -1 ? this.msgs.consent : this.msgs.required;
        }
      } else if (control.tagName === 'SELECT') {
        if (required && value === '') message = this.msgs.select;
      } else if (required && value === '') {
        message = this.msgs.required;
      } else if (value !== '' && control.type === 'email' && !EMAIL_RE.test(value)) {
        message = this.msgs.email;
      } else if (value !== '' && control.type === 'tel' && !PHONE_RE.test(value)) {
        message = this.msgs.phone;
      }

      this.setError(field, control, message);
      return message === '';
    }

    setError(field, control, message) {
      const slot = field.querySelector('[data-error]');
      if (message) {
        field.classList.add('is-error');
        control.setAttribute('aria-invalid', 'true');
        if (slot) {
          slot.textContent = message;
          slot.hidden = false;
          if (!slot.id) slot.id = control.id + '-error';
          control.setAttribute('aria-describedby', slot.id);
        }
      } else {
        field.classList.remove('is-error');
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-describedby');
        if (slot) {
          slot.hidden = true;
          slot.textContent = '';
        }
      }
    }

    /* ---------- custom dropdown ---------- */

    bindSelect(root) {
      const select = root.querySelector('select');
      const toggle = root.querySelector('[data-select-toggle]');
      const valueEl = root.querySelector('[data-select-value]');
      const list = root.querySelector('[data-select-list]');
      if (!select || !toggle || !list) return;

      const options = Array.from(list.querySelectorAll('[role="option"]'));
      const placeholder = valueEl.textContent;
      let activeIndex = -1;

      const open = () => {
        list.hidden = false;
        root.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        const current = options.findIndex((o) => o.dataset.value === select.value);
        setActive(current > -1 ? current : 0);
        document.addEventListener('click', onDocClick);
        document.addEventListener('keydown', onDocKey);
      };

      const close = (refocus) => {
        if (list.hidden) return;
        list.hidden = true;
        root.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        setActive(-1);
        document.removeEventListener('click', onDocClick);
        document.removeEventListener('keydown', onDocKey);
        if (refocus) toggle.focus();
      };

      const setActive = (index) => {
        activeIndex = index;
        options.forEach((o, i) => o.classList.toggle('is-active', i === index));
        if (index > -1) {
          toggle.setAttribute('aria-activedescendant', options[index].id || '');
          options[index].scrollIntoView({ block: 'nearest' });
        } else {
          toggle.removeAttribute('aria-activedescendant');
        }
      };

      const choose = (option) => {
        const value = option.dataset.value;
        select.value = value;
        options.forEach((o) => o.setAttribute('aria-selected', o === option ? 'true' : 'false'));
        valueEl.textContent = option.textContent.trim();
        toggle.classList.add('has-value');
        const native = select.options[select.selectedIndex];
        if (this.routeField) this.routeField.value = (native && native.dataset.route) || '';
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close(true);
      };

      const onDocClick = (e) => {
        if (!root.contains(e.target)) close(false);
      };

      const onDocKey = (e) => {
        if (list.hidden) return;
        switch (e.key) {
          case 'Escape':
            e.preventDefault();
            close(true);
            break;
          case 'ArrowDown':
            e.preventDefault();
            setActive(Math.min(activeIndex + 1, options.length - 1));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setActive(Math.max(activeIndex - 1, 0));
            break;
          case 'Home':
            e.preventDefault();
            setActive(0);
            break;
          case 'End':
            e.preventDefault();
            setActive(options.length - 1);
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            if (activeIndex > -1) choose(options[activeIndex]);
            break;
          case 'Tab':
            close(false);
            break;
          default:
            // type-ahead on first letter
            if (e.key.length === 1) {
              const k = e.key.toLowerCase();
              const idx = options.findIndex((o, i) => i > activeIndex && o.textContent.trim().toLowerCase().startsWith(k));
              const wrap = idx === -1 ? options.findIndex((o) => o.textContent.trim().toLowerCase().startsWith(k)) : idx;
              if (wrap > -1) setActive(wrap);
            }
        }
      };

      options.forEach((o, i) => {
        if (!o.id) o.id = select.id + '-opt-' + i;
        o.addEventListener('click', () => choose(o));
        o.addEventListener('mousemove', () => {
          if (activeIndex !== i) setActive(i);
        });
      });

      toggle.addEventListener('click', () => (list.hidden ? open() : close(false)));
      toggle.addEventListener('keydown', (e) => {
        if (list.hidden && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          open();
        }
      });

      // Field state for the toggle (focus/blur mirror the native control's classes).
      const field = root.closest('[data-field]');
      if (field) {
        toggle.addEventListener('focus', () => field.classList.add('is-active'));
        toggle.addEventListener('blur', () => {
          field.classList.remove('is-active');
          // Validate once the user has opened it and left without choosing.
          if (field.dataset.touched && list.hidden) this.validate(field);
        });
        toggle.addEventListener('click', () => (field.dataset.touched = '1'));
      }

      // Restore a re-rendered value (e.g. Shopify re-posted with errors).
      if (select.value) {
        const match = options.find((o) => o.dataset.value === select.value);
        if (match) {
          match.setAttribute('aria-selected', 'true');
          valueEl.textContent = match.textContent.trim();
          toggle.classList.add('has-value');
          const native = select.options[select.selectedIndex];
          if (this.routeField) this.routeField.value = (native && native.dataset.route) || '';
        }
      } else {
        valueEl.textContent = placeholder;
      }

      root._close = close;
      root._reset = () => {
        select.value = '';
        options.forEach((o) => o.setAttribute('aria-selected', 'false'));
        valueEl.textContent = placeholder;
        toggle.classList.remove('has-value');
        if (this.routeField) this.routeField.value = '';
      };
    }

    /* ---------- submit ---------- */

    onSubmit(e) {
      // Honeypot filled: quietly drop it.
      if (this.honeypot && this.honeypot.value) {
        e.preventDefault();
        return;
      }

      const invalid = this.fields.filter((f) => !this.validate(f));

      if (invalid.length) {
        e.preventDefault();
        this.showSummary(invalid.length);
        const control = this.control(invalid[0]);
        const target = invalid[0].querySelector('[data-select-toggle]') || control;
        if (target) {
          target.focus({ preventScroll: true });
          invalid[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }

      this.hideSummary();
      if (this.submitBtn) {
        this.submitBtn.classList.add('is-loading');
        this.submitBtn.setAttribute('aria-busy', 'true');
        if (this.submitLabel && this.dataset.sending) this.submitLabel.textContent = this.dataset.sending;
        // Disable after the current tick so the button's value still posts;
        // re-enable if the post never leaves the page (captcha dismissed, etc.).
        setTimeout(() => (this.submitBtn.disabled = true), 0);
        setTimeout(() => this.resetSubmit(), 12000);
        window.addEventListener('pageshow', () => this.resetSubmit(), { once: true });
      }
      // Let Shopify handle the post (it renders success/errors and hCaptcha).
    }

    resetSubmit() {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = false;
      this.submitBtn.classList.remove('is-loading');
      this.submitBtn.removeAttribute('aria-busy');
      if (this.submitLabel && this.submitLabel.dataset.label) this.submitLabel.textContent = this.submitLabel.dataset.label;
    }

    showSummary(count) {
      if (!this.summary) return;
      if (this.summaryText) this.summaryText.textContent = this.msgs.summary;
      this.summary.hidden = false;
      this.summary.dataset.count = count;
    }

    hideSummary() {
      if (this.summary) this.summary.hidden = true;
    }

    reset() {
      if (this.success) this.success.hidden = true;
      if (this.fieldsWrap) this.fieldsWrap.classList.remove('is-hidden');
      this.form.reset();
      this.fields.forEach((f) => {
        const c = this.control(f);
        delete f.dataset.touched;
        f.classList.remove('is-filled', 'is-active');
        if (c) this.setError(f, c, '');
      });
      this.form.querySelectorAll('[data-select]').forEach((s) => s._reset && s._reset());
      const first = this.control(this.fields[0]);
      if (first) first.focus();
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }

  customElements.define('contact-editorial', ContactEditorial);
})();
